"use client";

import { Card } from "@/components/ui/card";
import { usePodcastStatus } from "@/hooks/use-podcast-status";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import { Headphones, Loader2 } from "lucide-react";
import { PropertyPodcast } from "./property-podcast";

interface PodcastStatusProps {
  analysisId: string;
  className?: string;
}

export function PodcastStatus({ analysisId, className }: PodcastStatusProps) {
  const { data: podcastStatus, isLoading } = usePodcastStatus(analysisId);

  if (isLoading || !podcastStatus) return null;

  // Don't show anything if podcast is completed or not available
  if (
    podcastStatus.status === "completed" ||
    podcastStatus.status === "not_available"
  ) {
    return podcastStatus.podcast_url ? (
      <PropertyPodcast
        podcastUrl={podcastStatus.podcast_url}
        className="shadow-md hover:shadow-lg transition-shadow duration-300"
      />
    ) : null;
  }

  return (
    <Card
      className={cn(
        "relative overflow-hidden bg-white transition-shadow duration-300",
        className,
      )}
    >
      <div className="relative p-3 sm:p-4 md:p-6">
        <div className="flex items-start gap-2 sm:gap-4 md:gap-6">
          {/* Icon Column */}
          <div className="flex flex-col items-center gap-2">
            <div className="relative h-10 w-10 sm:h-12 sm:w-12 rounded-full border bg-primary/5 flex items-center justify-center">
              <motion.div
                className="absolute inset-0 rounded-full border-primary/30"
                animate={{
                  borderWidth: ["1px", "2px", "1px"],
                  scale: [1, 1.1, 1],
                }}
                transition={{
                  duration: 2,
                  repeat: Number.POSITIVE_INFINITY,
                  ease: "easeInOut",
                }}
              />
              <Loader2 className="h-5 w-5 sm:h-6 sm:w-6 text-primary animate-spin" />
            </div>
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold hidden sm:block">
              Processing
            </span>
          </div>

          {/* Main Content Column */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2 sm:mb-3">
              <Headphones className="h-4 w-4 text-primary" />
              <h3 className="text-sm font-semibold">AI Property Analysis</h3>
              <span className="text-xs text-muted-foreground">~5 min</span>
            </div>
            <p className="text-sm text-muted-foreground">
              We're creating a concise walkthrough of this property's features
              and potential. Check back in a couple minutes!
            </p>
          </div>
        </div>
      </div>
    </Card>
  );
}
